import { useState, useEffect } from "react";
import { UserPlus, Loader2, Save, CheckCircle2, Mail, BarChart3 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

const FIELDS = [["business_name", "Business name"], ["name", "Contact name"], ["email", "Email"], ["phone", "Phone"]];
export const errMsg = (err, f) => { const d = err.response?.data?.detail; return Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || f; };
const btn = "w-full font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-colors";

export const LeadDetails = ({ lead, isAdmin, onSaved }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState("");
  useEffect(() => { setF(Object.fromEntries([...FIELDS.map(([k]) => [k, lead[k] || ""]), ["notes", lead.notes || ""]])); }, [lead]);

  const run = async (key, fn, fallback) => {
    setBusy(key);
    try { return await fn(); } catch (err) { toast.error(errMsg(err, fallback)); return null; } finally { setBusy(""); }
  };
  const save = () => run("save", async () => {
    const { data } = await api.put(`/api/webadvisor/leads/${lead.id}/info`, f); onSaved(data);
    if (data.login_email_updated) data.invite_sent ? toast.success(`Saved — login email updated and a new login link was sent to ${data.email}`) : toast.warning(`Saved — login email updated. ${data.invite_error}`);
    else toast.success("Lead saved");
    return data;
  }, "Save failed");
  const convert = async () => {
    if (!(await save())) return;
    await run("convert", async () => {
      const { data } = await api.post(`/api/webadvisor/leads/${lead.id}/convert`, { origin_url: window.location.origin });
      onSaved({ ...lead, ...f, customer_user_id: data.customer_user_id, status: "Won" });
      toast.success(data.existing_account ? "Linked to the existing customer account" : data.invite_sent ? "Customer created — set-password email sent" : "Customer created (no invite email sent)");
    }, "Convert failed");
  };
  const invite = () => run("invite", async () => { const { data } = await api.post(`/api/webadvisor/leads/${lead.id}/invite`, { origin_url: window.location.origin }); onSaved({ ...lead, ...f, portal_invite_sent_at: data.sent_at }); toast.success(data.message); }, "Invite failed");
  const report = () => run("report", async () => { const { data } = await api.post(`/api/webadvisor/leads/${lead.id}/send-report`); toast.success(data.message); }, "Report failed");
  const spin = (k, Icon) => (busy === k ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />);

  return (
    <div className="space-y-3" data-testid="lead-details">
      {FIELDS.map(([k, l]) => <div key={k}><Label className="text-xs">{l}</Label><Input value={f[k] || ""} onChange={e => setF({ ...f, [k]: e.target.value })} data-testid={`lead-sheet-${k}`} /></div>)}
      <div><Label className="text-xs">Notes</Label><Textarea rows={5} value={f.notes || ""} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="lead-sheet-notes" /></div>
      <button onClick={save} disabled={!!busy} className={`${btn} bg-slate-900 hover:bg-slate-700 text-white`} data-testid="lead-sheet-save">{spin("save", Save)}Save</button>
      {isAdmin && (lead.customer_user_id ? (
        <p className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="lead-sheet-customer"><CheckCircle2 className="w-4 h-4" />This lead is a ShopAdvisor customer.</p>
      ) : (
        <div className="border-t border-slate-100 pt-3">
          <button onClick={convert} disabled={!!busy || !f.email} className={`${btn} bg-green-600 hover:bg-green-500 text-white`} data-testid="lead-sheet-convert">{spin("convert", UserPlus)}Turn into customer</button>
          <p className="text-xs text-slate-400 mt-1.5">Creates a customer account with Web Advisor enabled and emails them a link to set their password.</p>
        </div>
      ))}
      {lead.customer_user_id && (
        <div className="border-t border-slate-100 pt-3 space-y-3">
          <button onClick={invite} disabled={!!busy} className={`${btn} border border-blue-300 text-blue-700 hover:bg-blue-50`} data-testid="lead-sheet-invite">{spin("invite", Mail)}Email portal invite</button>
          <p className="text-xs text-slate-400" data-testid="lead-sheet-invite-info">{lead.portal_invite_sent_at ? `Last invite sent ${lead.portal_invite_sent_at.slice(0, 10)}. ` : ""}One-click link lets them set a password and land in their dashboard.</p>
          <button onClick={report} disabled={!!busy} className={`${btn} border border-slate-300 text-slate-700 hover:bg-slate-50`} data-testid="lead-sheet-send-report">{spin("report", BarChart3)}Email last month's traffic report</button>
          <p className="text-xs text-slate-400">Traffic reports go out automatically on the 1st of each month.</p>
        </div>
      )}
    </div>
  );
};
